import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "@/components/Loaders";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  FileEdit,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  Check,
  X,
  Edit2,
  Ban,
  ShoppingCart,
  CreditCard,
  ArrowRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Loader,
} from "lucide-react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";
import { getSelectUnities } from "@/Api/unities";
import { getSelectSellers } from "@/Api/sellers";
import {
  getSaleChangeRequests,
  getSaleChangeRequestShow,
  approveSaleChangeRequest,
  rejectSaleChangeRequest,
} from "@/Api/saleChangeRequests";

interface SaleChangeRequest {
  id: number;
  uuid: string;
  sale_id: number;
  type: "cancel" | "cancel_presale" | "edit";
  status: "pending" | "approved" | "rejected";
  reason_other: string | null;
  original_data: any;
  proposed_data: any;
  requester_notes: string | null;
  reviewer_notes: string | null;
  created_at: string;
  updated_at: string;
  sale: {
    id: number;
    uuid: string;
    total: string;
    created_at: string;
    client?: {
      name: string;
    };
    seller?: {
      user?: {
        name: string;
      };
    };
    unity?: {
      name: string;
    };
    sale_type?: {
      name: string;
    };
  };
  requester: {
    id: number;
    name: string;
  };
  reviewer?: {
    id: number;
    name: string;
  };
  reason?: {
    id: number;
    name: string;
  };
  unity?: {
    id: number;
    name: string;
  };
}

const STATUS_CONFIG = {
  pending: {
    label: "Pendente",
    color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    icon: Clock,
  },
  approved: {
    label: "Aprovado",
    color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    icon: CheckCircle2,
  },
  rejected: {
    label: "Rejeitado",
    color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    icon: XCircle,
  },
};

const TYPE_CONFIG = {
  cancel: {
    label: "Cancelamento",
    color: "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400",
    icon: Ban,
  },
  cancel_presale: {
    label: "Cancelamento (Pré-venda)",
    color: "bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400",
    icon: ShoppingCart,
  },
  edit: {
    label: "Edição",
    color: "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400",
    icon: Edit2,
  },
};

const AUTO_REFRESH_INTERVAL = 15;

export const PageList = () => {
  const user = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const [listData, setListData] = useState<any>({});
  const [state, setState] = useState("loading");
  const [_unities, setUnities] = useState<Array<{ id: number; name: string }>>([]);
  const [sellers, setSellers] = useState<Array<{ id: number; user: { id: number; name: string } }>>([]);

  // Read sale_id from URL params
  const urlSaleId = searchParams.get("sale_id");

  const [filters, setFilters] = useState<{
    status?: string[];
    type?: string[];
    unity_ids?: number[];
    sale_id?: number;
    client_name?: string;
    requester_id?: number[];
    start_date?: string;
    end_date?: string;
  }>({
    sale_id: urlSaleId ? Number(urlSaleId) : undefined,
  });

  const [datePeriod, setDatePeriod] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: "asc" | "desc" }>({
    column: "created_at",
    direction: "desc",
  });

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SaleChangeRequest | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const [reviewerNotes, setReviewerNotes] = useState("");
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Auto-refresh states
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [isPaused] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const userUnityIds = user?.user?.unity?.map((u: any) => u.id) || [];
  const isSeller = user?.user?.user_type_id === 3;

  const handleDatePeriodChange = (period: string) => {
    setDatePeriod(period);
    const today = new Date();
    let start_date: string | undefined;
    let end_date: string | undefined;

    if (period === "today") {
      start_date = today.toISOString().split("T")[0];
      end_date = start_date;
    } else if (period === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      start_date = yesterday.toISOString().split("T")[0];
      end_date = start_date;
    } else if (period === "thisWeek") {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      start_date = startOfWeek.toISOString().split("T")[0];
      end_date = today.toISOString().split("T")[0];
    } else if (period === "thisMonth") {
      start_date = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
      end_date = today.toISOString().split("T")[0];
    } else if (period === "all") {
      start_date = undefined;
      end_date = undefined;
    }

    if (period !== "custom") {
      setFilters((prev) => ({ ...prev, start_date, end_date }));
    }
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    (async () => {
      try {
        const [unitiesRes, sellersRes] = await Promise.all([
          getSelectUnities(),
          getSelectSellers({ is_active: true }),
        ]);
        if (unitiesRes.ok) {
          setUnities(unitiesRes.data?.response || []);
        }
        if (sellersRes.ok) {
          setSellers(sellersRes.data?.response || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    })();
  }, []);

  const fetchRequests = useCallback(async (page = 1, silent = false) => {
    if (!silent) {
      setState("loading");
    } else {
      setIsRefreshing(true);
    }
    try {
      const { data, ok } = await getSaleChangeRequests(page, {
        unity_ids: userUnityIds.length > 0 ? userUnityIds : undefined,
        status: filters.status && filters.status.length > 0 ? filters.status : undefined,
        type: filters.type && filters.type.length > 0 ? filters.type : undefined,
        requester_id: filters.requester_id && filters.requester_id.length > 0 ? filters.requester_id : undefined,
        sale_id: filters.sale_id || undefined,
        client_name: filters.client_name || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        order_by: sortConfig.column,
        order_direction: sortConfig.direction,
      });

      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      if (!silent) {
        toast.error("Erro ao carregar solicitações");
      }
    } finally {
      if (!silent) {
        setState("production");
      } else {
        setIsRefreshing(false);
      }
    }
  }, [userUnityIds, filters, sortConfig]);

  useEffect(() => {
    fetchRequests();
  }, [sortConfig]);

  // Auto-refresh com countdown
  useEffect(() => {
    if (isPaused) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchRequests(listData?.current_page || 1, true);
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isPaused, fetchRequests, listData?.current_page]);

  const handleApplyFilters = async () => {
    await fetchRequests(1);
  };

  const handleClearFilters = async () => {
    setFilters({});
    setDatePeriod("all");
    setState("loading");
    try {
      const { data, ok } = await getSaleChangeRequests(1, {
        unity_ids: userUnityIds.length > 0 ? userUnityIds : undefined,
        order_by: sortConfig.column,
        order_direction: sortConfig.direction,
      });
      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setState("production");
    }
  };

  const handleViewDetails = async (request: SaleChangeRequest) => {
    setSelectedRequest(request);
    setDetailsModalOpen(true);
    setIsLoadingDetails(true);

    try {
      const { data, ok } = await getSaleChangeRequestShow(request.uuid);
      if (ok && data?.response) {
        setSelectedRequest(data.response);
      }
    } catch (error) {
      console.error("Error fetching request details:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleOpenActionModal = (type: "approve" | "reject") => {
    setActionType(type);
    setReviewerNotes("");
    setActionModalOpen(true);
  };

  const handleAction = async () => {
    if (!selectedRequest) return;

    setIsProcessingAction(true);
    try {
      const actionFn = actionType === "approve" ? approveSaleChangeRequest : rejectSaleChangeRequest;
      const { ok } = await actionFn(selectedRequest.uuid, {
        reviewer_notes: reviewerNotes || undefined,
      });

      if (ok) {
        toast.success(
          actionType === "approve"
            ? "Solicitação aprovada com sucesso!"
            : "Solicitação rejeitada com sucesso!"
        );
        setActionModalOpen(false);
        setDetailsModalOpen(false);
        setSelectedRequest(null);
        await fetchRequests(listData?.current_page || 1);
      }
    } catch {
    } finally {
      setIsProcessingAction(false);
    }
  };

  return (
    <Main>
      <If condition={hasPermission("view-sale-change-requests")}>
        <PageHeader
          icon={FileEdit}
          title="Solicitações de Alteração"
          description="Gerencie solicitações de cancelamento e edição de vendas"
        >
          <button
            onClick={() => {
              setCountdown(AUTO_REFRESH_INTERVAL);
              fetchRequests(listData?.current_page || 1, true);
            }}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
            title="Atualizar agora"
          >
            {isRefreshing ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">Atualizar</span>
            <span className="text-xs bg-blue-600 px-1.5 py-0.5 rounded">{countdown}s</span>
          </button>
        </PageHeader>

        <div className="flex flex-col gap-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 items-end">
                <div>
                  <Label className="text-sm">ID da Venda</Label>
                  <Input
                    type="number"
                    placeholder="Ex: 123"
                    value={filters.sale_id || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        sale_id: e.target.value ? Number(e.target.value) : undefined,
                      }))
                    }
                  />
                </div>


                <div>
                  <Label className="text-sm">Cliente</Label>
                  <Input
                    placeholder="Nome do cliente"
                    value={filters.client_name || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        client_name: e.target.value || undefined,
                      }))
                    }
                  />
                </div>

                <div>
                  <Label className="text-sm">Solicitante</Label>
                  <Select
                    value={filters.requester_id?.[0]?.toString() || "all"}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        requester_id: value === "all" ? undefined : [Number(value)],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {sellers.map((seller) => (
                        <SelectItem key={seller.id} value={seller.user.id.toString()}>
                          {seller.user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm">Status</Label>
                  <Select
                    value={filters.status?.[0] || "all"}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: value === "all" ? undefined : [value],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="approved">Aprovado</SelectItem>
                      <SelectItem value="rejected">Rejeitado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm">Tipo</Label>
                  <Select
                    value={filters.type?.[0] || "all"}
                    onValueChange={(value) =>
                      setFilters((prev) => ({
                        ...prev,
                        type: value === "all" ? undefined : [value],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="cancel">Cancelamento</SelectItem>
                      <SelectItem value="cancel_presale">Cancelamento (Pré-venda)</SelectItem>
                      <SelectItem value="edit">Edição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm">Período</Label>
                  <Select value={datePeriod} onValueChange={handleDatePeriodChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="today">Hoje</SelectItem>
                      <SelectItem value="yesterday">Ontem</SelectItem>
                      <SelectItem value="thisWeek">Esta Semana</SelectItem>
                      <SelectItem value="thisMonth">Este Mês</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {datePeriod === "custom" && (
                  <>
                    <div>
                      <Label className="text-sm">Data Inicial</Label>
                      <Input
                        type="date"
                        value={filters.start_date || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, start_date: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Data Final</Label>
                      <Input
                        type="date"
                        value={filters.end_date || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, end_date: e.target.value }))
                        }
                      />
                    </div>
                  </>
                )}

                <div className="flex gap-2 col-span-2 md:col-span-1">
                  <Button onClick={handleApplyFilters} className="flex-1">Filtrar</Button>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Limpar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {state === "loading" ? (
            <CreateLoaderTable cols={9} lines={6} />
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <button
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={() => handleSort("sale_id")}
                        >
                          ID
                          {sortConfig.column === "sale_id" ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Solicitante</TableHead>
                      <TableHead>
                        <button
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={() => handleSort("created_at")}
                        >
                          Data
                          {sortConfig.column === "created_at" ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-50" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {listData?.data?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Nenhuma solicitação encontrada
                        </TableCell>
                      </TableRow>
                    ) : (
                      listData?.data?.map((request: SaleChangeRequest) => {
                        const typeConfig = TYPE_CONFIG[request.type];
                        const statusConfig = STATUS_CONFIG[request.status];
                        const TypeIcon = typeConfig.icon;
                        const StatusIcon = statusConfig.icon;

                        return (
                          <TableRow
                            key={request.id}
                            className={request.type === "cancel_presale" ? "bg-purple-50/50 dark:bg-purple-900/10" : undefined}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">#{request.sale?.id}</span>
                                <Eye
                                  className="h-4 w-4 text-blue-500 cursor-pointer hover:text-blue-700"
                                  onClick={() => navigate(`/vendas?sale_id=${request.sale?.id}`)}
                                />
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {request.sale?.client?.name || "Sem cliente"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">
                                {Mask.formatCurrency(Number(request.sale?.total || 0))}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}
                              >
                                <TypeIcon className="h-3 w-3" />
                                {typeConfig.label}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {request.reason?.name || request.reason_other || "-"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">{request.requester?.name}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {new Date(request.created_at).toLocaleDateString("pt-BR")}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                              >
                                <StatusIcon className="h-3 w-3" />
                                {statusConfig.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  onClick={() => handleViewDetails(request)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {request.status === "pending" && !isSeller && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => {
                                        setSelectedRequest(request);
                                        handleOpenActionModal("approve");
                                      }}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => {
                                        setSelectedRequest(request);
                                        handleOpenActionModal("reject");
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {listData?.last_page > 1 && (
                  <div className="flex justify-center p-4 border-t">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => fetchRequests(listData.current_page - 1)}
                            className={listData.current_page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                        {(() => {
                          const currentPage = listData.current_page;
                          const lastPage = listData.last_page;
                          const maxVisible = 5;
                          const pages: (number | string)[] = [];

                          if (lastPage <= maxVisible + 2) {
                            for (let i = 1; i <= lastPage; i++) pages.push(i);
                          } else {
                            pages.push(1);

                            let start = Math.max(2, currentPage - 1);
                            let end = Math.min(lastPage - 1, currentPage + 1);

                            if (currentPage <= 3) {
                              end = Math.min(maxVisible, lastPage - 1);
                            } else if (currentPage >= lastPage - 2) {
                              start = Math.max(2, lastPage - maxVisible + 1);
                            }

                            if (start > 2) pages.push("...");
                            for (let i = start; i <= end; i++) pages.push(i);
                            if (end < lastPage - 1) pages.push("...");

                            pages.push(lastPage);
                          }

                          return pages.map((page, idx) => (
                            <PaginationItem key={idx}>
                              {page === "..." ? (
                                <span className="px-3 py-2 text-muted-foreground">...</span>
                              ) : (
                                <PaginationLink
                                  onClick={() => fetchRequests(page as number)}
                                  isActive={currentPage === page}
                                  className={`cursor-pointer ${
                                    currentPage === page
                                      ? "bg-primary text-primary-foreground"
                                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                  }`}
                                >
                                  {page}
                                </PaginationLink>
                              )}
                            </PaginationItem>
                          ));
                        })()}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => fetchRequests(listData.current_page + 1)}
                            className={listData.current_page === listData.last_page ? "pointer-events-none opacity-50" : "cursor-pointer"}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Details Modal */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedRequest && (
                  <>
                    {selectedRequest.type === "edit" ? (
                      <Edit2 className="h-5 w-5 text-blue-500" />
                    ) : selectedRequest.type === "cancel_presale" ? (
                      <ShoppingCart className="h-5 w-5 text-purple-500" />
                    ) : (
                      <Ban className="h-5 w-5 text-red-500" />
                    )}
                    Detalhes da Solicitação
                  </>
                )}
              </DialogTitle>
            </DialogHeader>

            {isLoadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : selectedRequest ? (
              <div className="space-y-4">
                {/* Header com Status e Tipo */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${TYPE_CONFIG[selectedRequest.type].color}`}
                    >
                      {TYPE_CONFIG[selectedRequest.type].label}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedRequest.status].color}`}
                    >
                      {STATUS_CONFIG[selectedRequest.status].label}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(selectedRequest.created_at).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Venda</p>
                    <p className="font-semibold">#{selectedRequest.sale?.id}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Valor</p>
                    <p className="font-semibold text-green-600">
                      {Mask.formatCurrency(Number(selectedRequest.sale?.total || 0))}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Unidade</p>
                    <p className="font-medium">{selectedRequest.unity?.name || selectedRequest.sale?.unity?.name || "-"}</p>
                  </div>
                </div>

                {/* Cliente e Solicitação */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                      <p className="font-medium">{selectedRequest.sale?.client?.name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Solicitante</p>
                      <p className="font-medium">{selectedRequest.requester?.name}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Motivo</p>
                    <p className="font-medium">{selectedRequest.reason?.name || selectedRequest.reason_other || "-"}</p>
                  </div>
                </div>

                {/* Proposed Changes for Edit */}
                {selectedRequest.type === "edit" && selectedRequest.proposed_data && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <ArrowRight className="h-4 w-4" />
                      Alterações Propostas
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedRequest.proposed_data.sale_type_id &&
                        selectedRequest.proposed_data.sale_type_id !== selectedRequest.original_data?.sale_type_id && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Tipo de Venda:</span>
                            <span className="line-through text-red-500">
                              {selectedRequest.original_data?.sale_type_name || "Anterior"}
                            </span>
                            <ArrowRight className="h-3 w-3" />
                            <span className="text-green-600 font-medium">
                              {selectedRequest.proposed_data.sale_type_name || "Novo"}
                            </span>
                          </div>
                        )}
                      {selectedRequest.proposed_data.payment_forms && (
                        <div>
                          <span className="text-muted-foreground">Formas de Pagamento:</span>
                          <div className="mt-1 space-y-1">
                            {selectedRequest.proposed_data.payment_forms.map((pf: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-2 ml-4">
                                <CreditCard className="h-3 w-3 text-muted-foreground" />
                                <span>{pf.payment_form_name || `Forma ${pf.payment_form_id}`}</span>
                                <span className="font-medium">
                                  {Mask.formatCurrency(pf.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reviewer Info */}
                {selectedRequest.reviewer && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium text-sm">Avaliador</h4>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Por:</span>
                      <span className="ml-2">{selectedRequest.reviewer.name}</span>
                    </div>
                    {selectedRequest.reviewer_notes && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Observação:</span>
                        <p className="mt-1 text-gray-700 dark:text-gray-300">
                          {selectedRequest.reviewer_notes}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                {selectedRequest.status === "pending" && !isSeller && (
                  <DialogFooter className="gap-2">
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleOpenActionModal("reject")}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleOpenActionModal("approve")}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Aprovar
                    </Button>
                  </DialogFooter>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Action Modal */}
        <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {actionType === "approve" ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Aprovar Solicitação
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-500" />
                    Rejeitar Solicitação
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {actionType === "approve"
                  ? selectedRequest?.type === "cancel"
                    ? "A venda será cancelada ao aprovar esta solicitação."
                    : selectedRequest?.type === "cancel_presale"
                    ? "A pré-venda será rejeitada ao aprovar esta solicitação (nenhum estoque foi distribuído ainda, então nada precisa ser estornado)."
                    : "As alterações serão aplicadas ao aprovar esta solicitação."
                  : "A solicitação será rejeitada e nenhuma alteração será feita."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reviewer-notes">Observação (opcional)</Label>
                <Textarea
                  id="reviewer-notes"
                  value={reviewerNotes}
                  onChange={(e) => setReviewerNotes(e.target.value)}
                  placeholder="Adicione uma observação sobre sua decisão..."
                  className="min-h-[80px]"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setActionModalOpen(false)}
                disabled={isProcessingAction}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAction}
                disabled={isProcessingAction}
                className={
                  actionType === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }
              >
                {isProcessingAction ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processando...
                  </>
                ) : actionType === "approve" ? (
                  "Aprovar"
                ) : (
                  "Rejeitar"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </If>
    </Main>
  );
};
